import * as React from 'react'
import { format, parseISO } from 'date-fns'
import { Briefcase, Building2, CalendarRange, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { FilterChip, FilterClear, FilterOption } from '@/components/ui/filter-chip'
import { Input } from '@/components/ui/input'
import { EVENT_STATUSES } from '@lib/enums/event-lifecycle.enum'
import { EVENT_SEARCH_MAX, type EventListQuery, type EventStatusKey } from '@lib/input/event-list.input'
import type { EventPlannerOptionListDto } from '@lib/dto/event.dto'
import type { CalendarDay } from '@lib/validators/temporal.validator'

/** Derived event status (matches Events page tags). */
/**
 * Re-exported, not redeclared.
 *
 * This was a hand-written union — a third copy of a list that lives in
 * `event-lifecycle.enum.ts` — and it had already fallen behind: `cancelled`
 * existed on the wire and not here.
 */
export type { EventStatusKey } from '@lib/input/event-list.input'

export interface EventsFilterState {
  venueId: string | null
  /** Inclusive start day, or empty when the range is open on the left. */
  dateFrom: CalendarDay | ''
  /** Inclusive end day, or empty when the range is open on the right. */
  dateTo: CalendarDay | ''
  status: EventStatusKey | null
  /** Case-insensitive event-name fragment. Empty means no name filter. */
  query: string
  /** The planning company's id, `none` for events without a planner, or null for any. */
  planner: NonNullable<EventListQuery['planner']> | null
}

export const EMPTY_EVENTS_FILTERS: EventsFilterState = {
  venueId: null,
  dateFrom: '',
  dateTo: '',
  status: null,
  query: '',
  planner: null,
}

export function eventsFiltersActive(f: EventsFilterState): boolean {
  return (
    f.venueId !== null ||
    f.dateFrom !== '' ||
    f.dateTo !== '' ||
    f.status !== null ||
    f.query.trim() !== '' ||
    f.planner !== null
  )
}

const STATUS_LABELS: Record<EventStatusKey, string> = {
  enquiry: 'Inquiry',
  booked: 'Booked',
  past: 'Past',
  cancelled: 'Canceled',
  lost: 'Lost',
}

const STATUS_OPTIONS = EVENT_STATUSES.flatMap((key) => {
  const label = STATUS_LABELS[key]
  return label === undefined ? [] : [{ key, label }]
})

function formatRangeLabel(from: CalendarDay | '', to: CalendarDay | ''): string | null {
  if (from === '' && to === '') return null
  const fmt = (s: CalendarDay): string => format(parseISO(s), 'MMM d, yyyy')
  if (from !== '' && to !== '') return `${fmt(from)} – ${fmt(to)}`
  if (from !== '') return `from ${fmt(from)}`
  return to === '' ? null : `until ${fmt(to)}`
}

/**
 * The name search above the events table.
 *
 * Above the table rather than in its bar: it is the page's first question
 * ("which event?"), and a field wide enough to type into does not fit beside
 * the filters and the table's own button. A name search includes past events.
 */
export function EventsSearch({
  filters,
  onChange,
}: {
  filters: EventsFilterState
  onChange: (next: EventsFilterState) => void
}): React.ReactElement {
  const query = filters.query

  return (
    <label className="relative block max-w-[340px]">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
      <Input
        value={query}
        onChange={(e) => onChange({ ...filters, query: e.target.value })}
        placeholder="Search events…"
        maxLength={EVENT_SEARCH_MAX}
        className="pl-9 pr-8"
        aria-label="Search events by name"
      />
      {query.trim() !== '' ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange({ ...filters, query: '' })}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-ink-muted outline-none hover:text-ink focus-visible:focus-ds"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </label>
  )
}

/**
 * The events table's filters — venue, date range and derived status — for its
 * `actions` bar, beside "New event".
 *
 * `FilterChip`s rather than `FilterMenu`, because the API takes one venue, one
 * range and one status. The default (Upcoming) hides past events; Status → Past is how
 * they come back. "Clear" resets the search too, since it is one set of
 * conditions on one list.
 */
export function EventsFilterChips({
  filters,
  onChange,
  venues,
  showVenueFilter = true,
  planners,
}: {
  filters: EventsFilterState
  onChange: (next: EventsFilterState) => void
  venues: { id: string; name: string }[]
  /**
   * The companies planning these events. Empty hides the chip: with no planner
   * anywhere, "No planner" is every event.
   */
  planners: EventPlannerOptionListDto['planners']
  /**
   * Hide the venue chip when the venue is already chosen elsewhere — a venue
   * admin picks it in the sidebar switcher, so a second control here would offer
   * to filter away the only venue they are looking at.
   */
  showVenueFilter?: boolean
}): React.ReactElement {
  const venueName = venues.find((v) => v.id === filters.venueId)?.name ?? null
  const statusLabel = STATUS_OPTIONS.find((s) => s.key === filters.status)?.label ?? null
  const rangeLabel = formatRangeLabel(filters.dateFrom, filters.dateTo)
  const plannerLabel =
    filters.planner === 'none'
      ? 'None'
      : (planners.find((p) => p.vendorId === filters.planner)?.name ?? null)
  const active = eventsFiltersActive(filters)

  // A fragment, so the chips wrap in the bar's own row with the table's button
  // rather than as a block of their own.
  return (
    <>
      {showVenueFilter ? (
      <FilterChip
        property="Venue"
        valueLabel={venueName}
        icon={Building2}
        onClear={() => onChange({ ...filters, venueId: null })}
      >
        {(close) => (
          <>
            <FilterOption
              selected={filters.venueId === null}
              onSelect={() => {
                onChange({ ...filters, venueId: null })
                close()
              }}
            >
              Any venue
            </FilterOption>
            {venues.map((v) => (
              <FilterOption
                key={v.id}
                selected={filters.venueId === v.id}
                onSelect={() => {
                  onChange({ ...filters, venueId: v.id })
                  close()
                }}
              >
                {v.name}
              </FilterOption>
            ))}
            {venues.length === 0 ? (
              <p className="px-2 py-2 text-sm text-ink-muted">No venues yet.</p>
            ) : null}
          </>
        )}
      </FilterChip>
      ) : null}

      <FilterChip
        property="Date"
        valueLabel={rangeLabel}
        icon={CalendarRange}
        onClear={() => onChange({ ...filters, dateFrom: '', dateTo: '' })}
      >
        {() => (
          <div className="space-y-3 p-2">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-ink-muted">From</p>
              <DatePicker
                value={filters.dateFrom}
                onChange={(dateFrom) => onChange({ ...filters, dateFrom })}
                placeholder="Start date"
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-ink-muted">To</p>
              <DatePicker
                value={filters.dateTo}
                onChange={(dateTo) => onChange({ ...filters, dateTo })}
                placeholder="End date"
              />
            </div>
            {rangeLabel ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => onChange({ ...filters, dateFrom: '', dateTo: '' })}
              >
                Clear dates
              </Button>
            ) : null}
          </div>
        )}
      </FilterChip>

      <FilterChip
        property="Status"
        valueLabel={statusLabel}
        onClear={() => onChange({ ...filters, status: null })}
      >
        {(close) => (
          <>
            <FilterOption
              selected={filters.status === null}
              onSelect={() => {
                onChange({ ...filters, status: null })
                close()
              }}
            >
              Upcoming
            </FilterOption>
            {STATUS_OPTIONS.map((s) => (
              <FilterOption
                key={s.key}
                selected={filters.status === s.key}
                onSelect={() => {
                  onChange({ ...filters, status: s.key })
                  close()
                }}
              >
                {s.label}
              </FilterOption>
            ))}
          </>
        )}
      </FilterChip>

      {planners.length > 0 || filters.planner !== null ? (
        <FilterChip
          property="Planner"
          valueLabel={plannerLabel}
          icon={Briefcase}
          onClear={() => onChange({ ...filters, planner: null })}
        >
          {(close) => {
            const pick = (planner: EventsFilterState['planner']): void => {
              onChange({ ...filters, planner })
              close()
            }
            return (
              <>
                <FilterOption selected={filters.planner === null} onSelect={() => pick(null)}>
                  Any planner
                </FilterOption>
                <FilterOption selected={filters.planner === 'none'} onSelect={() => pick('none')}>
                  No planner
                </FilterOption>
                {planners.map((p) => (
                  <FilterOption key={p.vendorId} selected={filters.planner === p.vendorId} onSelect={() => pick(p.vendorId)}>
                    {p.name}
                  </FilterOption>
                ))}
              </>
            )
          }}
        </FilterChip>
      ) : null}

      {active ? <FilterClear onClear={() => onChange(EMPTY_EVENTS_FILTERS)} /> : null}
    </>
  )
}
